import { Component, OnInit, ChangeDetectorRef, OnDestroy, Inject, Renderer2 } from '@angular/core';
import { IonApp, IonButton, IonButtons, IonContent, IonFooter, IonHeader, IonLabel, IonListHeader, IonMenu, IonMenuButton, IonTitle, IonToolbar, Platform, IonItem,IonList, IonIcon, IonMenuToggle } from '@ionic/angular/standalone';
import { Router, RouterModule, NavigationEnd, ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';

import { KeycloakAuthService } from '../services/auth/auth.service';
import { filter, map, first } from 'rxjs/operators';
import { Subscription, combineLatest } from 'rxjs';
import { Card, Button } from '../model/card-buttons.model'; 
import { provideEchartsCore } from 'ngx-echarts';
import * as echarts from 'echarts/core';
import { BarChart, GaugeChart, LineChart, PieChart } from 'echarts/charts';
import { DataZoomComponent, GridComponent, LegendComponent, TitleComponent, TooltipComponent } from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';
import { addIcons } from 'ionicons';
import { documentTextOutline, openOutline, close, statsChartOutline, trendingUpOutline, analyticsOutline, listOutline, filterOutline, calendar, menuOutline,settingsOutline, saveOutline,closeOutline,refreshOutline,addCircleOutline,fingerPrintOutline, chevronForwardOutline, chevronBackOutline,ellipsisVerticalOutline,personOutline,appsOutline,documentAttachOutline, layersOutline,downloadOutline,fileTrayFullOutline, gridOutline, rocketOutline, heart } from 'ionicons/icons';
import { SharedDataService } from '../services/centraliced-routes-cards.service';
import { DOCUMENT } from '@angular/common';

echarts.use([
  LineChart, DataZoomComponent, BarChart, GridComponent, CanvasRenderer,
  LegendComponent, TooltipComponent, PieChart, TitleComponent, GaugeChart
]);

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterModule, IonMenu, IonMenuToggle, IonButtons, IonLabel, IonHeader, IonToolbar, IonMenuButton, IonTitle, IonContent, IonFooter, IonApp, IonListHeader, IonItem, IonIcon, IonList],
  templateUrl:'./app.component.html',
  styleUrls: ['./app.component.css'],
  providers: [ provideEchartsCore({ echarts })]
})
export class AppComponent implements OnInit, OnDestroy {
  subtitle: string = '';
  isLoggedIn = false;
  appReady = false; 
  buttons: Button[] = [];
  visibleMenuCards: Card[] = [];
  isHeaderHidden = false;
  lastScrollTop = 0;
  private routerEventsSubscription: Subscription | undefined;
  private authorizedItemsSubscription: Subscription | undefined; 
  private authSubscription: Subscription | undefined;

  // Propiedades para el menú dinámico
  departments: string[] = [];
  showSubMenu = false;
  selectedDepartment = '';
  cardsForSubMenu: Card[] = [];

  constructor(
    private renderer: Renderer2,
    @Inject(DOCUMENT) private document: Document,
    private keycloakAuthService: KeycloakAuthService,
    private sharedDataService: SharedDataService,
    private router: Router,
    private cdr: ChangeDetectorRef,
    private platform: Platform
  ) {
    addIcons({
      'document-text-outline': documentTextOutline, 'open-outline': openOutline, 'close': close,
      'stats-chart-outline': statsChartOutline, 'trending-up-outline': trendingUpOutline,
      'analytics-outline': analyticsOutline, 'list-outline': listOutline, 'filter-outline': filterOutline,
      'calendar-week': calendar, 'menu-outline': menuOutline,
      'save-outline' : saveOutline, 'settings-outline': settingsOutline,
      'close-outline':closeOutline, 'refresh-outline':refreshOutline,
      'add-circle-outline':addCircleOutline, 'finger-print-outline':fingerPrintOutline,
      'chevron-forward-outline': chevronForwardOutline, 'chevron-back-outline': chevronBackOutline,
      'ellipsis-vertical-outline': ellipsisVerticalOutline,
      'person-outline': personOutline, 'apps-outline': appsOutline,
      'document-attach-outline': documentAttachOutline,
      'layers-outline': layersOutline,
      'download-outline':downloadOutline,
      'file-tray-outline':fileTrayFullOutline,
      'grid-outline': gridOutline,
      'rocket-outline': rocketOutline,
      'heart': heart

    });
  }


  ngOnInit() {
    this.departments = this.sharedDataService.allDepartments;
    this.authSubscription = this.keycloakAuthService.authenticated$.subscribe((loggedIn: boolean) => {
      this.isLoggedIn = loggedIn;
    });
    this.sharedDataService.authorizedButtons$.subscribe(buttons => {
      this.buttons = buttons;
    });
    this.subscribeToData();
    this.initializeApp();
  }

  async initializeApp() {
    await this.platform.ready().then(async () => {
      this.renderer.removeClass(this.document.body, 'dark');
      if (this.platform.is('capacitor')) {
      }
    });

    await this.keycloakAuthService.authReady$.pipe(first(ready => ready === true)).toPromise();
    
    this.isLoggedIn = this.keycloakAuthService.isLoggedIn();
    
    if (!this.isLoggedIn) {
      await this.router.navigateByUrl('/Login', { replaceUrl: true });
    } else {
      await this.router.navigateByUrl('/Home', { replaceUrl: true });
    }
    
    setTimeout(() => {
      this.appReady = true;
    }, 0);
  }

  subscribeToData() {
    this.authorizedItemsSubscription = combineLatest([
      this.keycloakAuthService.authReady$,
      this.keycloakAuthService.authorizedCards$,
      this.keycloakAuthService.authorizedButtons$ 
    ]).subscribe(([authReady, authorizedCards, authorizedButtons]) => {
      if (authReady) {
        this.visibleMenuCards = authorizedCards; 
        this.buttons = authorizedButtons; 
      } else {
        this.visibleMenuCards = []; 
        this.buttons = []; 
      }
      this.cdr.detectChanges();
    });

    this.routerEventsSubscription = this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      map(() => {
        const snapshot: RouterStateSnapshot = this.router.routerState.snapshot;
        const deepestSnapshot = this.getDeepestChildSnapshot(snapshot.root);
        return deepestSnapshot.data;
      })
    ).subscribe((data) => {
      this.subtitle = data && data['subtitle'] ? data['subtitle'] : '';
      this.cdr.detectChanges();
    });
  }

  private getDeepestChildSnapshot(snapshot: ActivatedRouteSnapshot): ActivatedRouteSnapshot {
    let route = snapshot;
    while (route.firstChild && route.firstChild.outlet === 'primary') {
      route = route.firstChild;
    }
    return route;
  }

  showDepartmentSubMenu(department: string): void {
    this.selectedDepartment = department;
    this.cardsForSubMenu = this.sharedDataService.getCardsByDepartment(department);
    this.showSubMenu = true;
  }

  backToMainMenu(): void {
    this.showSubMenu = false;
    this.selectedDepartment = '';
    this.cardsForSubMenu = [];
  }
  
  getButtonsForCard(cardTitle: string): Button[] {
    return this.buttons.filter(button => button.condition === cardTitle);
  }

  ngOnDestroy(): void {
    if (this.routerEventsSubscription) this.routerEventsSubscription.unsubscribe();
    if (this.authorizedItemsSubscription) this.authorizedItemsSubscription.unsubscribe();
  }

  onActivate(event: any): void {
    this.isLoggedIn = this.keycloakAuthService.isLoggedIn();
  }

  logout() {
    this.keycloakAuthService.logout();
  }

  goToLink(url: string){
    window.open(url, "_blank");
  }
}